import React, { useEffect, useState } from "react";
import { calculateAge, isDobWithinAgeRange } from "../../common/utils/formatters";
import DynamicFormField from "../../common/components/DynamicFormField";
import { Modal } from "../../common/components";
import {
  GENDER_RESTRICTED_AGE_ERROR,
  violatesGenderRestrictedAge,
} from "../../common/utils/genderAgeRestriction";

/** Relation with joint-life primary annuitant (UI + API payload). */
export const SECONDARY_ANNUITANT_RELATION_OPTIONS = [
  "Spouse",
  "Son",
  "Daughter",
  "Brother",
  "Father",
  "Mother",
  "Sister",
  "Father-in-law",
  "Mother-in-law",
  "Others",
];

const ANNUITANT_MAX_AGE = 75;
const GENDER_OPTIONS = ["Male", "Female", "Other"].map((option) => ({
  label: option,
  value: option,
}));
const FALLBACK_RELATION_OPTIONS = SECONDARY_ANNUITANT_RELATION_OPTIONS.map((option) => ({
  label: option,
  value: option,
  bizCode: "",
}));
const sanitizeNameValue = (value) => value.replace(/[^A-Za-z\s]/g, "");

/** Hyphen, underscore, and spacing variants of the same relation compare equal. */
export function normalizeRelationLabel(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ");
}



/**
 * Joint-life relation dropdown from `relationshipdetails`.
 * Only relations the BI option map accepts are kept. Falls back to the
 * static list when the master has not loaded.
 */
export function buildSecondaryAnnuitantRelationOptions(relationshipRows) {
  const rows = Array.isArray(relationshipRows) ? relationshipRows : [];
  const allowed = new Set(
    SECONDARY_ANNUITANT_RELATION_OPTIONS.map(normalizeRelationLabel),
  );
  const fromMaster = [];
  const seen = new Set();
  rows.forEach((row) => {
    const label = String(row?.relation ?? "").trim();
    const key = normalizeRelationLabel(label);
    if (!label || !allowed.has(key) || seen.has(key)) return;
    seen.add(key);
    fromMaster.push({
      label,
      value: label,
      bizCode: String(row?.relationshipBizCode ?? "").trim(),
    });
  });
  return fromMaster.length ? fromMaster : FALLBACK_RELATION_OPTIONS;
}

/** Master's `relationshipBizCode` for a stored relation label. */
export function resolveSecondaryAnnuitantRelationBizCode(relationshipRows, relation) {
  const key = normalizeRelationLabel(relation);
  if (!key) return "";
  const rows = Array.isArray(relationshipRows) ? relationshipRows : [];
  const row = rows.find((item) => normalizeRelationLabel(item?.relation) === key);
  return String(row?.relationshipBizCode ?? "").trim();
}

const JointLifeAnnuitantModal = ({
  isOpen,
  onClose,
  form,
  onChange,
  onSave,
  secondaryAnnuitantMinAge,
  relationOptions,
}) => {
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [showAgeCriteriaModal, setShowAgeCriteriaModal] = useState(false);
  useEffect(() => {
    if (isOpen) setShowValidationErrors(false);
  }, [isOpen]);
  if (!isOpen) return null;
  const name = String(form?.secondaryAnnuitantName ?? "").trim();
  const dob = String(form?.secondaryAnnuitantDob ?? "").trim();
  const gender = String(form?.secondaryAnnuitantGender ?? "").trim();
  const relation = String(form?.secondaryAnnuitantRelation ?? "").trim();
  const resolvedRelationOptions = relationOptions?.length
    ? relationOptions
    : FALLBACK_RELATION_OPTIONS;
  const matchedRelation = resolvedRelationOptions.find(
    (option) => normalizeRelationLabel(option.value) === normalizeRelationLabel(relation),
  );
  const relationValue = matchedRelation?.value ?? relation;
  const dobAge = dob ? calculateAge(dob) : null;
  const dobAgeError =
    dob &&
    (dobAge === null ||
      !isDobWithinAgeRange(dob, {
        minAge: secondaryAnnuitantMinAge,
        maxAge: ANNUITANT_MAX_AGE,
      }))
      ? `Annuitant age must be between ${secondaryAnnuitantMinAge} and ${ANNUITANT_MAX_AGE} years.`
      : "";
  const errors = {
    secondaryAnnuitantName: !name ? "Please enter name." : "",
    secondaryAnnuitantDob: !dob ? "Please select date of birth." : dobAgeError,
    secondaryAnnuitantGender: !gender ? "Please select gender." : "",
    secondaryAnnuitantRelation: !relation ? "Please select relation." : "",
  };

  const isSaveDisabled = Object.values(errors).some(Boolean);
  const handleFieldChange = (e) => {
    const { name: fieldName, value: fieldValue } = e.target;
    const nextValue =
      fieldName === "secondaryAnnuitantName"
        ? sanitizeNameValue(fieldValue)
        : fieldValue;
    onChange(fieldName, nextValue);
  };
  const handleSaveClick = () => {
    if (isSaveDisabled) {
      setShowValidationErrors(true);
      return;
    }
    if (
      violatesGenderRestrictedAge(
        form?.secondaryAnnuitantGender,
        form?.secondaryAnnuitantDob,
      )
    ) {
      setShowAgeCriteriaModal(true);
      return;
    }
    onSave?.();
  };

  return (
    <div className="edit-modal-backdrop" onClick={onClose}>
      <div
        className="edit-modal-container edit-step-modal  session"
        role="dialog"
        aria-modal="true"
        aria-label="Secondary annuitant details"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="edit-modal-header">
          <h5 className="edit-modal-title light">Secondary Annuitant Details</h5>
          <button className="edit-modal-close" onClick={onClose} aria-label="Close dialog">
          <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.781 13.7198C14.8507 13.7895 14.906 13.8722 14.9437 13.9632C14.9814 14.0543 15.0008 14.1519 15.0008 14.2504C15.0008 14.349 14.9814 14.4465 14.9437 14.5376C14.906 14.6286 14.8507 14.7114 14.781 14.781C14.7114 14.8507 14.6286 14.906 14.5376 14.9437C14.4465 14.9814 14.349 15.0008 14.2504 15.0008C14.1519 15.0008 14.0543 14.9814 13.9632 14.9437C13.8722 14.906 13.7895 14.8507 13.7198 14.781L7.50042 8.56073L1.28104 14.781C1.14031 14.9218 0.94944 15.0008 0.750417 15.0008C0.551394 15.0008 0.360523 14.9218 0.219792 14.781C0.0790615 14.6403 3.92322e-09 14.4494 0 14.2504C-3.92322e-09 14.0514 0.0790615 13.8605 0.219792 13.7198L6.4401 7.50042L0.219792 1.28104C0.0790615 1.14031 0 0.94944 0 0.750417C0 0.551394 0.0790615 0.360523 0.219792 0.219792C0.360523 0.0790615 0.551394 0 0.750417 0C0.94944 0 1.14031 0.0790615 1.28104 0.219792L7.50042 6.4401L13.7198 0.219792C13.8605 0.0790615 14.0514 -3.92322e-09 14.2504 0C14.4494 3.92322e-09 14.6403 0.0790615 14.781 0.219792C14.9218 0.360523 15.0008 0.551394 15.0008 0.750417C15.0008 0.94944 14.9218 1.14031 14.781 1.28104L8.56073 7.50042L14.781 13.7198Z" fill="#3C3D3D"></path></svg>
          </button>
        </div>
        <div className="edit-modal-contents edit-step-modal-fields">
          <DynamicFormField
            field={{
              fieldReference: "secondaryAnnuitantName",
              type: "text",
              label: "Full Name",
              placeholder: "Annuitant Name",
            }}
            value={form.secondaryAnnuitantName ?? ""}
            onChange={handleFieldChange}
            error={showValidationErrors ? errors.secondaryAnnuitantName : null}
          />
          <DynamicFormField
            field={{
              fieldReference: "secondaryAnnuitantDob",
              type: "date",
              label: "Date of Birth",
            }}
            value={form.secondaryAnnuitantDob ?? ""}
            onChange={handleFieldChange}
            error={showValidationErrors ? errors.secondaryAnnuitantDob : null}
            useDdMmYyyyForDob
            minAge={secondaryAnnuitantMinAge}
            maxAge={ANNUITANT_MAX_AGE}
          />
          <DynamicFormField
            field={{
              fieldReference: "secondaryAnnuitantGender",
              type: "select",
              label: "Gender",
              placeholder: "Select gender",
              options: GENDER_OPTIONS,
            }}
            value={form.secondaryAnnuitantGender ?? ""}
            onChange={handleFieldChange}
            error={showValidationErrors ? errors.secondaryAnnuitantGender : null}
          />
          <DynamicFormField
            field={{
              fieldReference: "secondaryAnnuitantRelation",
              type: "select",
              label: "Relation",
              placeholder: "Select relation",
              options: resolvedRelationOptions,
            }}
            value={relationValue}
            onChange={handleFieldChange}
            error={showValidationErrors ? errors.secondaryAnnuitantRelation : null}
          />

                <Modal
                  isOpen={showAgeCriteriaModal}
                  onClose={() => setShowAgeCriteriaModal(false)}
                  title="Product Eligibility Not Met"
                  type="error"
                  btnTitle="OK"
                >
                 <p>The information provided does not satisfy the eligibility requirements for this product. Please check the details entered and try again.</p>
                </Modal>

        </div>
        <div className="edit-modal-footer">
          <button className="primary-button btn regular " onClick={handleSaveClick}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default JointLifeAnnuitantModal;
